import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Page } from "@/components/layout";
import { useLeaderScope } from "@/hooks/useLeaderScope";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { canViewSchoolTeamInsights } from "@/services/teamsService";
import { SchoolTeamPage } from "./SchoolTeamPage";
import { DistrictSchoolsPage } from "./DistrictSchoolsPage";

/**
 * Routes the leader to the right My Teams view based on their scope:
 *
 * - `/admin/my-teams`              → district school list (district leaders / superintendents)
 *                                    → school dashboard (principal)
 *                                    → sample dashboard (no scope yet)
 * - `/admin/my-teams/:schoolId`    → that school's aggregate patterns (district drill-down)
 */
export const MyTeamsPage = () => {
  const { schoolId } = useParams<{ schoolId?: string }>();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);
  const { scope, isLoading } = useLeaderScope();

  if (schoolId) {
    return (
      <SchoolTeamDrillDown
        schoolId={schoolId}
        scope={scope}
        scopeLoading={isLoading}
        persona={persona}
      />
    );
  }

  if (isLoading) {
    return (
      <Page.Container>
        <Page.Header />
        <Page.Content>
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        </Page.Content>
      </Page.Container>
    );
  }

  const isDistrictView =
    persona === "district_leader" || scope.kind === "district" || scope.kind === "super_admin";

  if (isDistrictView) {
    const districtId = scope.kind === "district" ? scope.districtId : null;
    return (
      <DistrictSchoolsPage
        districtId={districtId}
        viewMode={persona === "district_leader" ? "teams" : "district"}
        canManageOrganizationContext={scope.kind === "district"}
      />
    );
  }

  if (scope.kind === "school") {
    return <SchoolTeamPage schoolId={scope.schoolId} canManageOrganizationContext />;
  }

  return <SchoolTeamPage schoolId={null} canManageOrganizationContext={false} />;
};

function SchoolTeamDrillDown({
  schoolId,
  scope,
  scopeLoading,
  persona,
}: {
  schoolId: string;
  scope: ReturnType<typeof useLeaderScope>["scope"];
  scopeLoading: boolean;
  persona: ReturnType<typeof deriveWorkspacePersona>;
}) {
  const [access, setAccess] = useState<"pending" | "allowed" | "denied">("pending");

  useEffect(() => {
    if (scopeLoading) return;

    let cancelled = false;
    setAccess("pending");
    void canViewSchoolTeamInsights(schoolId, scope).then((allowed) => {
      if (!cancelled) setAccess(allowed ? "allowed" : "denied");
    });

    return () => {
      cancelled = true;
    };
  }, [schoolId, scope, scopeLoading]);

  const backTo =
    scope.kind === "district" || scope.kind === "super_admin" || persona === "district_leader"
      ? {
          to: "/admin/my-teams",
          label: persona === "district_leader" ? "Back to all schools" : "Back to district",
        }
      : undefined;

  if (scopeLoading || access === "pending") {
    return (
      <Page.Container>
        <Page.Header />
        <Page.Content>
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        </Page.Content>
      </Page.Container>
    );
  }

  if (access === "denied") {
    return (
      <Page.Container>
        <Page.Header className="space-y-2">
          {backTo && (
            <Link
              to={backTo.to}
              className="inline-block font-inter text-xs text-white/70 transition-colors hover:text-white"
            >
              ← {backTo.label}
            </Link>
          )}
          <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">My Team</h1>
        </Page.Header>
        <Page.Content>
          <div className="rounded-2xl border border-stone-200 bg-white px-6 py-10 text-center">
            <p className="font-inter text-sm text-stone-600 md:text-base">
              You don&apos;t have access to aggregate patterns for this school.
            </p>
          </div>
        </Page.Content>
      </Page.Container>
    );
  }

  return <SchoolTeamPage
    schoolId={schoolId}
    backTo={backTo}
    canManageOrganizationContext={scope.kind === "district" || (scope.kind === "school" && scope.schoolId === schoolId)}
  />;
}
